import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Payment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const checkPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Vérification du paiement expirée');
      return;
    }

    setChecking(true);
    try {
      const response = await axios.get(`${API}/payments/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Paiement réussi !');
        setTimeout(() => navigate('/auth?mode=signup'), 2000);
      } else if (response.data.status === 'expired') {
        setPaymentStatus('failed');
        toast.error('Session de paiement expirée');
      } else {
        setTimeout(() => checkPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      toast.error('Erreur de vérification');
    } finally {
      setChecking(false);
    }
  };

  const handleStripePayment = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const formData = new FormData();
      formData.append('origin_url', originUrl);

      const response = await axios.post(`${API}/payments/checkout`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Erreur de paiement');
      setLoading(false);
    }
  };

  if (sessionId && checking) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="glass-card p-12 text-center max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pink-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Vérification du paiement...</h2>
          <p className="text-gray-600">Veuillez patienter</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="glass-card p-12 text-center max-w-md">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Paiement réussi !</h2>
          <p className="text-gray-600 mb-6">Vous pouvez maintenant créer votre compte</p>
          <button
            onClick={() => navigate('/auth?mode=signup')}
            className="btn-primary w-full py-4"
          >
            Continuer l'inscription
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="glass-card p-12 text-center max-w-md">
          <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Échec du paiement</h2>
          <p className="text-gray-600 mb-6">Votre paiement n'a pas abouti</p>
          <button
            onClick={() => navigate('/payment')}
            className="btn-primary w-full py-4"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="glass-card p-12 max-w-2xl w-full">
        <div className="text-center mb-8">
          <CreditCard className="w-16 h-16 mx-auto mb-4 text-pink-500" />
          <h2 className="text-3xl font-bold gradient-text mb-2">Inscription Premium</h2>
          <p className="text-gray-600">Les 500 premiers inscrits gratuits ont été atteints</p>
        </div>

        <div className="bg-white rounded-2xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Inscription A-More AI</h3>
              <p className="text-gray-600">Accès complet à la plateforme</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold gradient-text">10$</div>
              <div className="text-sm text-gray-500">Paiement unique</div>
            </div>
          </div>

          <div className="space-y-3 mb-8">
            <div className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Matching IA illimité</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Chat avec suggestions IA</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Profil optimisé</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Support prioritaire</span>
            </div>
          </div>

          <button
            data-testid="stripe-payment-btn"
            onClick={handleStripePayment}
            disabled={loading}
            className="w-full btn-primary py-4 flex items-center justify-center gap-2"
          >
            {loading ? 'Redirection...' : 'Payer avec Stripe'}
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-pink-600"
            style={{ transition: 'color 0.2s' }}
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
}
